import * as React from "react";
import * as Sentry from "@sentry/react";

/**
 * Defensive stopgap for the production "destroy is not a function" crash.
 *
 * React stores whatever a useEffect/useLayoutEffect callback returns and calls
 * it as the cleanup ("destroy") on unmount. If that value is not a function
 * (and not undefined), React throws "destroy is not a function" while
 * committing a deleted tree, which the top-level ErrorBoundary catches and
 * blanks the app. In production (Chromium 150) something is handing React a
 * non-function cleanup on route-transition unmounts.
 *
 * This guard patches React's hook dispatcher so any effect returning a
 * non-function cleanup is neutralized (React sees `undefined` and skips it),
 * and reports the offending value to Sentry so we can pin down the root cause
 * from real traffic. It is intentionally narrow (only touches useEffect /
 * useLayoutEffect return values) and fails closed if React internals are
 * absent (e.g. a future React upgrade), so it can never itself break the app.
 *
 * TODO(remove once root cause is fixed): this reaches into React 18 internals.
 * Revisit on any React major upgrade.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
type Cleanup = ReturnType<React.EffectCallback>;

// Pure, exported for testing: wrap an effect callback so a non-function return
// is reported and swallowed. A real function cleanup (or undefined) passes
// through untouched.
export const guardCleanup = (
  fn: unknown,
  onBadCleanup: (value: unknown) => void,
): unknown => {
  if (typeof fn !== "function") return fn;

  return function guardedEffect(this: unknown, ...args: unknown[]): Cleanup {
    const ret = (fn as (...a: unknown[]) => unknown).apply(this, args);
    if (ret !== undefined && typeof ret !== "function") {
      onBadCleanup(ret);
      return undefined;
    }
    return ret as Cleanup;
  };
};

const MAX_REPORTS = 5;
let reportCount = 0;

const reportBadCleanup = (value: unknown): void => {
  if (reportCount >= MAX_REPORTS) return;
  reportCount += 1;
  try {
    Sentry.captureMessage("Neutralized non-function effect cleanup", {
      level: "warning",
      fingerprint: ["effect-cleanup-guard"],
      extra: {
        valueType: typeof value,
        value: String(value),
        stack: new Error("non-function effect cleanup").stack,
      },
    });
  } catch {
    // Reporting is best-effort; never let it surface.
  }
  if (import.meta.env.VITE_ENVIRONMENT === "development") {
    // eslint-disable-next-line no-console
    console.warn("[effectCleanupGuard] neutralized non-function cleanup:", value);
  }
};

export const installEffectCleanupGuard = (): void => {
  const internals: any = (React as any)
    .__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED;
  const holder = internals?.ReactCurrentDispatcher;
  if (!holder) return;

  let inner = holder.current;
  const cache = new WeakMap<object, any>();

  const wrapDispatcher = (dispatcher: any): any => {
    if (!dispatcher) return dispatcher;
    const cached = cache.get(dispatcher);
    if (cached) return cached;

    const proxy = new Proxy(dispatcher, {
      get(target, prop, receiver) {
        const value = Reflect.get(target, prop, receiver);
        if (prop === "useEffect" || prop === "useLayoutEffect") {
          return (cb: unknown, deps: unknown) =>
            value(guardCleanup(cb, reportBadCleanup), deps);
        }
        return value;
      },
    });
    cache.set(dispatcher, proxy);
    return proxy;
  };

  Object.defineProperty(holder, "current", {
    configurable: true,
    get() {
      return wrapDispatcher(inner);
    },
    set(value) {
      inner = value;
    },
  });

  if (import.meta.env.VITE_ENVIRONMENT === "development") {
    // eslint-disable-next-line no-console
    console.info("[effectCleanupGuard] installed");
  }
};
